import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../store/auth';
import { useUI } from '../store/ui';
import { useT } from '../lib/i18n';

// Public marketing landing page, ported from docs/DEMO_REFERENCE.html:5247-5338.
// Layout mirrors the demo's `#screen-landing` section: top nav with brand mark
// + sign-in / start-trial buttons, navy hero with H1 + sub + CTAs + trust line +
// seal mascot, "Built for the chair" 4-feature grid, navy "How it works"
// 3-step block, "Ready to run a tighter chair?" CTA card, copyright footer.
//
// Authenticated users get redirected to /today on mount — the landing page is
// for visitors only. Sign-in / sign-up CTAs route to the auth screens which
// share their own quote panel layout.
export function Landing() {
  const session = useAuth((s) => s.session);
  const language = useUI((s) => s.language);
  const setLanguage = useUI((s) => s.setLanguage);
  const t = useT();
  const navigate = useNavigate();

  // Bounce authed users to the app — runs after mount so React Router can settle
  // first. Visitors with no session see the landing page normally.
  useEffect(() => {
    if (session) navigate('/today', { replace: true });
  }, [session, navigate]);

  return (
    <div className="min-h-[100dvh] bg-bg text-ink-900">
      {/* Top nav — brand mark + sign-in / start-trial / language toggle */}
      <nav className="absolute top-0 left-0 right-0 z-20 px-6 md:px-10 pt-6 md:pt-7">
        <div className="max-w-page mx-auto flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 group">
            <span className="w-10 h-10 md:w-11 md:h-11 rounded-[11px] grid place-items-center overflow-hidden">
              <img
                src="/assets/busy-icon.png"
                alt=""
                className="w-full h-full object-cover"
              />
            </span>
            <span className="font-display tracking-[0.04em] leading-none text-xl md:text-2xl text-white">
              BUSY <span className="text-gold-400">BARBER</span>
            </span>
          </Link>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setLanguage(language === 'en' ? 'es' : 'en')}
              className="hidden sm:inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold tracking-wide rounded-full bg-white/8 text-white/80 border border-white/12 hover:bg-white/15"
              title="Toggle language / Cambiar idioma"
            >
              <span className={language === 'en' ? 'text-white' : 'text-white/55'}>EN</span>
              <span className="text-white/30">·</span>
              <span className={language === 'es' ? 'text-white' : 'text-white/55'}>ES</span>
            </button>
            <Link
              to="/sign-in"
              className="px-3 py-1.5 md:px-4 md:py-2 text-sm font-bold rounded-full text-white/85 hover:text-white hover:bg-white/10 transition-colors"
            >
              {t('land.nav.signin')}
            </Link>
            <Link
              to="/sign-up"
              className="px-4 py-2 text-sm font-bold rounded-full bg-gold-500 text-navy-900 hover:bg-gold-400 transition-colors"
            >
              {t('land.nav.start')}
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero — navy gradient with H1, sub, CTAs, trust line, seal mascot */}
      <section
        className="relative text-white pt-32 md:pt-36 pb-20 md:pb-24 px-6 md:px-10 overflow-hidden"
        style={{
          background:
            'radial-gradient(ellipse at 78% 25%, rgba(212,169,66,.16), transparent 55%), radial-gradient(ellipse at 18% 85%, rgba(200,16,46,.14), transparent 55%), linear-gradient(180deg, #0d1424 0%, #131e3a 100%)',
        }}
      >
        <div className="max-w-page mx-auto grid md:grid-cols-[1.1fr_0.9fr] gap-10 md:gap-16 items-center relative z-[1]">
          <div>
            <h1 className="font-display text-[44px] md:text-[64px] lg:text-[72px] leading-[1.05] mb-5">
              {t('land.h1.daily')}{' '}
              <span className="text-gold-400">{t('land.h1.grow')}</span>
              <br />
              {t('land.h1.your')}{' '}
              <span className="text-red-600">{t('land.h1.chair')}</span>
            </h1>
            <p className="text-base md:text-lg leading-relaxed text-white/78 max-w-[540px] mb-8">
              {t('land.sub')}
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/sign-up"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-xl bg-gold-500 text-navy-900 font-bold text-[15px] hover:bg-gold-400 transition-colors"
              >
                {t('land.cta.trial')}
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/sign-in"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-xl border border-white/30 text-white font-bold text-[15px] hover:bg-white/10 transition-colors"
              >
                {t('land.nav.signin')}
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-3 md:gap-4 text-[13px] text-white/60 flex-wrap">
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-flex gap-0.5 text-gold-400">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Star key={i} className="w-3.5 h-3.5" />
                  ))}
                </span>
                <span>{t('land.trust.rating')}</span>
              </span>
              <span className="w-1 h-1 rounded-full bg-white/30" />
              <span>{t('land.trust.platform')}</span>
              <span className="w-1 h-1 rounded-full bg-white/30" />
              <span>{t('land.trust.cancel')}</span>
            </div>
          </div>
          <div className="grid place-items-center">
            <div
              className="w-[280px] md:w-[420px] lg:w-[480px] max-w-full"
              style={{ filter: 'drop-shadow(0 24px 60px rgba(0,0,0,.55)) drop-shadow(0 0 30px rgba(212,169,66,.18))' }}
            >
              <img src="/assets/busy-seal.png" alt="Busy Barber" className="w-full h-auto" />
            </div>
          </div>
        </div>
      </section>

      {/* Features — "Four things every barber needs" */}
      <section className="py-20 md:py-24 px-6 md:px-10 bg-bg">
        <div className="max-w-page mx-auto">
          <div className="text-center md:text-left max-w-2xl">
            <SectionEyebrow tone="red">{t('land.section1.eyebrow')}</SectionEyebrow>
            <h2 className="font-display text-[34px] md:text-[44px] lg:text-[52px] leading-[1.05] text-navy-900 dark:text-gold-500 mb-3">
              {t('land.section1.h2')}
            </h2>
            <p className="text-ink-500 text-lg max-w-xl">{t('land.section1.lede')}</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-5 mt-12 max-w-4xl mx-auto md:mx-0">
            <FeatureCard
              icon={<ClockIcon className="w-6 h-6" />}
              title={t('land.feature1.title')}
              body={t('land.feature1.body')}
            />
            <FeatureCard
              icon={<BarChartIcon className="w-6 h-6" />}
              title={t('land.feature2.title')}
              body={t('land.feature2.body')}
            />
            <FeatureCard
              icon={<CameraIcon className="w-6 h-6" />}
              title={t('land.feature3.title')}
              body={t('land.feature3.body')}
            />
            <FeatureCard
              icon={<MessageIcon className="w-6 h-6" />}
              title={t('land.feature4.title')}
              body={t('land.feature4.body')}
            />
          </div>
        </div>
      </section>

      {/* How it works — navy, three numbered steps */}
      <section className="py-20 md:py-24 px-6 md:px-10 bg-navy-900 text-white relative overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at 50% 0%, rgba(212,169,66,.08), transparent 60%)',
          }}
        />
        <div className="max-w-page mx-auto relative">
          <div className="text-center md:text-left max-w-2xl">
            <SectionEyebrow tone="gold">{t('land.section2.eyebrow')}</SectionEyebrow>
            <h2 className="font-display text-[34px] md:text-[44px] lg:text-[52px] leading-[1.05] text-white mb-3">
              {t('land.section2.h2')}
            </h2>
            <p className="text-white/65 text-lg max-w-xl">{t('land.section2.lede')}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5 mt-12">
            <StepCard num="01" title={t('land.step1.title')} body={t('land.step1.body')} />
            <StepCard
              num="02"
              title={t('land.step2.title')}
              titleNote={t('land.step2.optional')}
              body={t('land.step2.body')}
            />
            <StepCard num="03" title={t('land.step3.title')} body={t('land.step3.body')} />
          </div>
        </div>
      </section>

      {/* CTA banner */}
      <section className="py-16 md:py-20 px-6 md:px-10 bg-bg border-t border-line">
        <div className="max-w-page mx-auto">
          <div
            className="relative rounded-3xl bg-navy-900 border border-navy-700 p-8 md:p-14 text-white flex flex-col md:flex-row items-start md:items-center justify-between gap-6 md:gap-10 shadow-xl overflow-hidden"
          >
            <span
              aria-hidden
              className="absolute bottom-0 left-0 right-0 h-1"
              style={{ background: 'linear-gradient(90deg, #c8102e, #d4a942)' }}
            />
            <div className="max-w-xl">
              <h2 className="font-display text-[34px] md:text-[42px] leading-tight text-white mb-2.5">
                {t('land.cta.h2')}
              </h2>
              <p className="text-white/72 text-base md:text-lg">{t('land.cta.sub')}</p>
            </div>
            <Link
              to="/sign-up"
              className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-xl bg-gold-500 text-navy-900 font-bold text-[15px] hover:bg-gold-400 transition-colors shrink-0"
            >
              {t('land.cta.start')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <footer className="bg-navy-900 text-white/55 text-center text-[13px] py-8 px-6">
        {t('land.footer')}
      </footer>
    </div>
  );
}

/* ============================ subcomponents ============================ */

function SectionEyebrow({ children, tone }: { children: React.ReactNode; tone: 'red' | 'gold' }) {
  const color = tone === 'red' ? 'text-red-600' : 'text-gold-400';
  const bar = tone === 'red' ? 'bg-red-600' : 'bg-gold-400';
  return (
    <div className={`inline-flex items-center gap-2.5 font-extrabold text-xs tracking-[0.18em] uppercase mb-3 ${color}`}>
      <span className={`inline-block w-7 h-0.5 ${bar}`} />
      {children}
    </div>
  );
}

function FeatureCard({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="rounded-2xl bg-surface border border-line p-7 transition-all hover:-translate-y-0.5 hover:shadow-md hover:border-line-strong">
      <div className="w-12 h-12 rounded-xl bg-navy-900 text-gold-400 grid place-items-center mb-4">
        {icon}
      </div>
      <h3 className="font-extrabold text-lg md:text-xl text-navy-900 dark:text-gold-500 mb-2 leading-snug">
        {title}
      </h3>
      <p className="text-ink-500 text-[15px] leading-relaxed">{body}</p>
    </div>
  );
}

function StepCard({
  num,
  title,
  titleNote,
  body,
}: {
  num: string;
  title: string;
  titleNote?: string;
  body: string;
}) {
  return (
    <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-7">
      <div className="font-display text-5xl text-gold-400 leading-none mb-3">{num}</div>
      <h3 className="font-extrabold text-lg md:text-xl text-white mb-2 leading-snug">
        {title}
        {titleNote && (
          <span className="font-semibold opacity-70 ml-1.5">{titleNote}</span>
        )}
      </h3>
      <p className="text-white/70 text-[15px] leading-relaxed">{body}</p>
    </div>
  );
}

/* ============================ icons ============================ */

function ArrowRight({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

function Star({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function BarChartIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <line x1="12" y1="20" x2="12" y2="10" />
      <line x1="18" y1="20" x2="18" y2="4" />
      <line x1="6" y1="20" x2="6" y2="16" />
    </svg>
  );
}

function CameraIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}

function MessageIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}
